import { NavLink, Navigate, Route, Routes, useLocation, useNavigate } from 'react-router-dom';
import { ErrorBoundary } from './components/ErrorBoundary.js';
import { CalendarIcon, ChartIcon, HeartIcon, HomeIcon, LifebuoyIcon, PrinterIcon, ShareIcon } from './components/Icons.js';
import { useSession } from './lib/session.js';
import { CheckIn } from './screens/CheckIn.js';
import { Export } from './screens/Export.js';
import { History } from './screens/History.js';
import { Home } from './screens/Home.js';
import { Journal } from './screens/Journal.js';
import { HomeworkPage } from './screens/HomeworkPage.js';
import { HomeworkNew } from './screens/HomeworkNew.js';
import { HomeworkIndex } from './screens/HomeworkIndex.js';
import { HomeworkSheet } from './screens/HomeworkSheet.js';
import { JournalEntry } from './screens/JournalEntry.js';
import { Medications } from './screens/Medications.js';
import { Settings } from './screens/Settings.js';
import { SharedView } from './screens/SharedView.js';
import { SharePreview } from './screens/SharePreview.js';
import { SharedDocumentPage } from './screens/Documents.js';
import { Month } from './screens/Month.js';
import { Days } from './screens/Days.js';
import { Sos } from './screens/Sos.js';
import { ThoughtRecordDetail } from './screens/ThoughtRecordDetail.js';
import { ThoughtRecordNew } from './screens/ThoughtRecordNew.js';
import { WeekPage } from './screens/WeekPage.js';
import { Trends } from './screens/Trends.js';
import { People } from './screens/People.js';
import { Circle } from './screens/Circle.js';
import { PersonPage } from './screens/PersonPage.js';
import { Welcome } from './screens/Welcome.js';
import { Worksheets } from './screens/Worksheets.js';
import { LifeEventPage } from './screens/LifeEventPage.js';

/**
 * Routing, in two tiers: the one page that belongs to a visitor, and everything
 * else, which belongs to the person signed in.
 */
export function App(): JSX.Element {
  return (
    <Routes>
      {/*
        This route has to resolve before anything reads the session, and the
        reason is easy to lose when the two tiers get tidied back together.

        A share link is opened by a specialist who has no account on this server
        and never will. With the sign-in gate above the routing, as it was, they
        were handed the owner's sign-in screen and then bounced to "/" by the
        catch-all — so every link this app can produce was dead the moment it
        reached a browser, and nothing about the failure said so.

        Hoisting the route was necessary but not sufficient: the session hook's
        loading and error branches used to return *above* the router, so the
        public page still could not render until two requests the visitor has no
        stake in had resolved, and a failure on either replaced the share with
        "Can't reach the server". A public page must not depend on the session
        bootstrap succeeding, so the gate now lives inside Owner, below.

        The wildcard sibling catches a link a mail client wrapped or truncated.
        Landing on the share page, which explains itself, beats landing on a
        sign-in screen for an account the reader does not have.

        SharedView renders its own page shell and no navigation on purpose:
        there is nowhere in the app for a visitor to go, and every link into it
        would be a sign-in wall for someone with nothing to sign in with.
      */}
      {/* The whole page, behind the document (the fixes pass). */}
      <Route
        path="/shared/:token/page"
        element={
          <ErrorBoundary>
            <SharedView full />
          </ErrorBoundary>
        }
      />
      {/* The two documents a link's page offers (release 5b): the brief for a
          therapist link, the week for a partner link. Same payload, same
          shell, a different reading order. Before the page route so the
          literal segment wins. */}
      <Route
        path="/shared/:token/:doc"
        element={
          <ErrorBoundary>
            <SharedDocumentPage />
          </ErrorBoundary>
        }
      />
      <Route
        path="/shared/:token"
        element={
          <ErrorBoundary>
            <SharedView />
          </ErrorBoundary>
        }
      />
      <Route
        path="/shared/*"
        element={
          <ErrorBoundary>
            <SharedView />
          </ErrorBoundary>
        }
      />
      <Route path="*" element={<Owner />} />
    </Routes>
  );
}

/** The sign-in gate, and the boot states that belong only to the owner's app. */
function Owner(): JSX.Element {
  const { user, loading, error } = useSession();
  // Subscribed, so the error branch can switch to Help now without a page load.
  // Owner is a fixed element under <Route path="*"> and would not re-render on a
  // route change otherwise.
  const { pathname } = useLocation();
  const navigate = useNavigate();

  if (loading) {
    return (
      <div className="app" style={{ display: 'grid', placeItems: 'center', minHeight: '100dvh' }}>
        <div className="spinner" aria-label="Loading" />
      </div>
    );
  }

  if (error) {
    /*
     * Help now still opens when nothing else does.
     *
     * `Sos.tsx` says of the bundled crisis card that it is "the one thing in the
     * app that must never depend on a request succeeding", and then this branch
     * replaced the entire app — the crisis screen with it — the moment the
     * session bootstrap rejected. On a train, in a basement, or while the box
     * restarts, the manifest's own "I need help now" home-screen shortcut landed
     * on a Try again button.
     *
     * Handled here rather than by hoisting the route above this gate, because a
     * second `/sos` route out there would shadow the real one and take the
     * bottom navigation with it. Sos already knows how to fail: its own request
     * fails the same way, and it renders the offline card with the numbers
     * compiled into the bundle.
     */
    if (pathname === '/sos') {
      return (
        <ErrorBoundary>
          <main className="app">
            <Sos />
          </main>
        </ErrorBoundary>
      );
    }
    return (
      <div className="app">
        <div className="page-header">
          <h1>Can't reach the server</h1>
          <p>{error}</p>
        </div>
        <button className="btn btn-primary btn-block" onClick={() => window.location.reload()}>
          Try again
        </button>
        {/*
          A route change, not a reload. This reloaded onto /sos, and with no
          network a page load is answered only from what the service worker has
          stored — almost never /sos, which is reached by tapping inside the app —
          so the one button this screen offers "on a train, in a basement"
          opened a blank page. The branch above reads the router's location, so
          switching here renders Help now at once from what is already on the
          phone; its own request fails and it shows the numbers in the bundle.
        */}
        <button
          className="btn btn-secondary btn-block"
          style={{ marginTop: 'var(--space-3)' }}
          onClick={() => navigate('/sos')}
        >
          I need help right now
        </button>
      </div>
    );
  }

  /*
   * Signed out, /sos still shows the numbers.
   *
   * The session-error branch above already carves this out; being signed out is
   * the same situation from the person's side and had no carve-out at all, so
   * the manifest's "I need help now" shortcut on a phone whose cookie had
   * expired landed on a password form. Sos handles it: its own request fails
   * with a 401, and it renders the bundled offline card — hard-coded numbers,
   * no network, no session.
   */
  if (!user && window.location.pathname === '/sos') {
    return (
      <ErrorBoundary>
        <main className="app">
          <Sos />
        </main>
      </ErrorBoundary>
    );
  }

  return user ? <SignedIn /> : <Welcome />;
}

/** Everything behind the gate. Every screen here assumes a signed-in owner. */
function SignedIn(): JSX.Element {
  /*
   * Release 6 of 2.0: the screens with two columns' worth of material — Days,
   * Insights, and since the desktop pass (14 Oct 2026) Circle, whose people
   * go two-up — take a wider column beside the rail. Everything else keeps a
   * reading measure. See `.app-wide` in global.css.
   */
  const { pathname } = useLocation();
  const wide = pathname.startsWith('/days') || pathname.startsWith('/trends') || pathname.startsWith('/circle');
  return (
    <>
      <main className={wide ? 'app app-wide' : 'app'}>
        <Routes>
          <Route path="/" element={<Home />} />
          <Route path="/sos" element={<Sos />} />
          <Route path="/check-in" element={<CheckIn />} />
          <Route path="/history" element={<History />} />
          {/* Release 6 of 2.0: the calendar before the list. The list stays at /history. */}
          <Route path="/days" element={<Days />} />
          <Route path="/days/:month" element={<Days />} />
          <Route path="/journal" element={<Journal />} />
          <Route path="/journal/:id" element={<JournalEntry />} />
          <Route path="/homework" element={<HomeworkIndex />} />
          <Route path="/homework/new" element={<HomeworkNew />} />
          <Route path="/homework/:id" element={<HomeworkPage />} />
          <Route path="/homework/:id/sheet" element={<HomeworkSheet />} />
          {/* PROTOTYPE — being tried before it is committed to. */}
          <Route path="/life-event/:id" element={<LifeEventPage />} />
          {/* Release 2 of 2.0: the people in the record, owner-only. */}
          <Route path="/people" element={<People />} />
          {/* Release 5 of 2.0: sharing around people. The share screen stays for the full list. */}
          <Route path="/circle" element={<Circle />} />
          <Route path="/people/:id" element={<PersonPage />} />
          <Route path="/thought-record" element={<ThoughtRecordNew />} />
          <Route path="/thought-record/:id" element={<ThoughtRecordDetail />} />
          {/* The Monday is the id — see WeekPage. The list that feeds this
              stays on the Log, because that fetch is what writes the weeks. */}
          <Route path="/week/:weekStart" element={<WeekPage />} />
          <Route path="/trends" element={<Trends />} />
          {/* Gated like everything else here. There is no public view of this
              one and there must not be: a share link exposes what its scopes
              allow, and a medication list is the single most identifying thing
              in the record. */}
          <Route path="/medications" element={<Medications />} />
          <Route path="/export" element={<Export />} />
          {/* Reads nothing of yours — no records, no requests, no database at
              all — so it is the one screen here that could technically sit above
              the gate. It stays below it anyway: it renders inside the owner's
              shell, and a signed-out visitor landing on it would get a bottom
              nav where every tab is a sign-in wall, plus a "Back to today"
              button leading to one. Empty paper is not worth that. */}
          <Route path="/worksheets" element={<Worksheets />} />
          {/* The September share screen is gone (the fixes pass, 13 Oct 2026):
              Circle holds the whole ladder on each person's card. */}
          <Route path="/share" element={<Navigate to="/circle" replace />} />
          {/* "See what they see": the link's own page, inside the owner's shell,
              fetched with his session and never recorded as an opening. */}
          <Route path="/share/:id/preview" element={<SharePreview />} />
          <Route path="/share/:id/preview/:doc" element={<SharePreview />} />
          {/* The month, for him (release 5b). No month means this one. */}
          <Route path="/month" element={<Month />} />
          <Route path="/month/:month" element={<Month />} />
          <Route path="/settings" element={<Settings />} />
          <Route path="*" element={<Navigate to="/" replace />} />
        </Routes>
      </main>

      <nav className="nav" aria-label="Main">
        <div className="nav-inner">
          <NavLink to="/" end>
            <HomeIcon />
            <span>Today</span>
          </NavLink>
          {/* Release 6 of 2.0 (PRD §1): Today · Days · Circle · Help now. Days is
              the calendar with the list behind it; Insights sits behind Days.
              The pitch called the first tab "Now"; it stays "Today" here
              because every screen and the whole manual call it that. */}
          <NavLink to="/days">
            <CalendarIcon />
            <span>Days</span>
          </NavLink>
          <NavLink to="/circle">
            <HeartIcon />
            <span>Circle</span>
          </NavLink>
          <NavLink to="/sos">
            <LifebuoyIcon />
            <span>Help now</span>
          </NavLink>
          {/* On the rail only (≥900px): the things a laptop has room to name.
              Hidden under the four tabs on a phone, where they are reached from
              Today, Days and Settings as before. */}
          <NavLink to="/trends" className="nav-rail-only">
            <ChartIcon />
            <span>Insights</span>
          </NavLink>
          <NavLink to="/medications" className="nav-rail-only">
            <span className="nav-glyph" aria-hidden="true">℞</span>
            <span>Medications</span>
          </NavLink>
          <NavLink to="/export" className="nav-rail-only">
            <PrinterIcon />
            <span>Print</span>
          </NavLink>
          <NavLink to="/settings" className="nav-rail-only">
            <ShareIcon />
            <span>Settings</span>
          </NavLink>
        </div>
      </nav>
    </>
  );
}
